import { useEffect, useState, type ReactNode } from "react";
import { Flame, History, Receipt } from "lucide-react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  Button,
  ActionBar,
  Field,
  FormCard,
  Modal,
  SelectField,
  StatusBadge,
  Tabs,
  TextareaField,
  Tile,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { formatDate, formatDateTime, formatEuro } from "../i18n/format";
import {
  HandelingenTegel,
  VolgendeStapPaneel,
  useDossierStappen,
} from "./CrematieDossierStappen";
import { CrematieDossierLogboek } from "./CrematieDossierLogboek";
import { CrematieDossierVervoerTegel } from "./CrematieDossierVervoerTegel";
import { CrematieDossierDierTegel } from "./CrematieDossierDierTegel";
import { CrematieDossierCrematieTegel } from "./CrematieDossierCrematieTegel";
import { CrematieDossierAfdrukken } from "./CrematieDossierAfdrukken";
import { CrematieDossierFactuurblok } from "./CrematieDossierFactuurblok";
import {
  CrematieDocumentError,
  downloadCrematieDocument,
  uploadCrematieDocument,
} from "../api/crematieDocument";
import { afdrukHtml, drukAf, type AfdrukDossier } from "./crematoriumAfdrukken";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium (Epic 21, MR-2): the read-only Crematiedossier detail — the React port of the Jmix
// `CrematiedossierDetailView` (display only; status transitions / product + payment CRUD / document
// generation are deferred to a follow-up write MR, mirroring the animal/relaties read-first
// migrations). GET uses a RELATIVE url. ROLE_CREMATORIUM_VIEW is server-enforced.

interface Eigenaar {
  naam: string | null;
  adres: string | null;
  postcode: string | null;
  plaats: string | null;
  telefoon: string | null;
  email: string | null;
  geenEmail: boolean | null;
}
interface Dier {
  naam: string | null;
  diersoort: string | null;
  ras: string | null;
  geslacht: string | null;
  kleur: string | null;
  gewichtKg: number | null;
  chipTatoeage: string | null;
  datumOverlijden: string | null;
  geboortedatum: string | null;
  kruising: boolean | null;
  kruisingMet: string | null;
}
interface Regel {
  id: string;
  omschrijving: string | null;
  aantal: number | null;
  prijs: number | null;
  kortingPct: number | null;
  btwPercentage: number | null;
  prijsHerkomst: string | null;
  volgorde: number | null;
  version: number | null;
  regelTotaal: number | null;
}
interface Betaling {
  id: string;
  betaaldOp: string | null;
  betaalwijze: string | null;
  bedrag: number | null;
  geregistreerdDoor: string | null;
}
interface StatusLog {
  id: string;
  vanStatus: string | null;
  naarStatus: string | null;
  tijdstip: string | null;
  gebruiker: string | null;
  opmerking: string | null;
}
interface FactuurRegel {
  id: string;
  factuurNummer: string | null;
  factuurDatum: string | null;
  vervaldatum: string | null;
  betaalStatus: string | null;
  totaalBedrag: number | null;
  openstaandBedrag: number | null;
}
interface Document {
  id: string;
  type: string | null;
  omschrijving: string | null;
  status: string | null;
  kanaal: string | null;
  aangemaaktOp: string | null;
  aangemaaktDoor: string | null;
  /** Gevuld bij een aangeleverd bestand; leeg bij een document dat uit een sjabloon komt. */
  bestandsnaam: string | null;
  grootte: number | null;
}
interface Controle {
  id: string;
  stap: string | null;
  uitkomst: string | null;
  identificatieNummer: string | null;
  gecontroleerdDoor: string | null;
  locatie: string | null;
  tijdstip: string | null;
  opmerking: string | null;
}
interface DossierDetail {
  id: string;
  conceptNummer: string | null;
  dossierNummer: string | null;
  status: string;
  verwerkingsvorm: string | null;
  uitvaartvormId: string | null;
  herkomst: string | null;
  spoed: boolean | null;
  aanmeldDatum: string | null;
  identificatieNummer: string | null;
  betaalStatus: string | null;
  planningDatum: string | null;
  crematoriumLocatie: string | null;
  crematieNummer: string | null;
  ritNummer: string | null;
  chauffeur: string | null;
  ontvangenDoor: string | null;
  ophaalAdres: string | null;
  ophaalDatum: string | null;
  afstandKm: number | null;
  asUrnNummer: string | null;
  retourwijze: string | null;
  grafnummer: string | null;
  herinneringOp: string | null;
  belmomentOp: string | null;
  incassoOp: string | null;
  factuurPerEmail: boolean | null;
  ingevoerdDoor: string | null;
  ophaalTijd: string | null;
  retourRekenen: boolean | null;
  ophaalAdresSoort: string | null;
  ritKosten: {
    tariefNaam: string | null;
    km: number;
    starttarief: number;
    extraKm: number;
    bedrag: number;
    btwPercentage: number | null;
    retour: boolean;
    spoed: boolean;
  } | null;
  asKlaarSinds: string | null;
  bijzonderheden: string | null;
  afgerondDatum: string | null;
  annuleringReden: string | null;
  eigenaar: Eigenaar | null;
  betalerNaam: string | null;
  /** De klant waar deze uitvaart aan hangt; leeg bij een losse aanmelding van vroeger. */
  relatieId: number | null;
  dierenartsRelatieId: number | null;
  derdePartijRelatieId: number | null;
  gewichtsklasseId: string | null;
  tariefId: string | null;
  tariefWijzigbaar: boolean;
  gewichtsklasseNaam: string | null;
  gewichtsklasseHandmatig: boolean | null;
  gewichtsklasseReden: string | null;
  runNummer: string | null;
  dier: Dier | null;
  subtotaal: number | null;
  kortingTotaal: number | null;
  btwTotaal: number | null;
  totaalBedrag: number | null;
  betaaldBedrag: number | null;
  openstaandBedrag: number | null;
  regels: Regel[];
  betalingen: Betaling[];
  statuslog: StatusLog[];
  facturen: FactuurRegel[];
  documenten: Document[];
  controles: Controle[];
}

const STATUS_TONE: Record<string, StatusTone> = {
  AFGEROND: "success",
  GECREMEERD: "success",
  GEANNULEERD: "danger",
  WACHT_OP_BETALING: "warning",
};

/** De regel uit het tarief die de crematie zelf is — niet de rit, die komt ook uit een tarief. */
function tariefRegel(regels: Regel[]) {
  const r = regels.find((x) => x.prijsHerkomst === "TARIEF" && !(x.omschrijving ?? "").startsWith("Ophaalkosten"));
  return r ? { omschrijving: r.omschrijving, prijs: r.prijs, btwPercentage: r.btwPercentage } : null;
}

interface StatusOpties {
  huidige: string | null;
  toegestaan: string[];
  volgende: string | null;
}

type StatusNotice = "conflict" | "forbidden" | "error";

// Crematorium (Epic 21, MR-W1): the first write surface on the detail screen — dossier status
// transitions. Reads status-opties (current + allowed targets + happy-path next) and POSTs a
// transition; on success refetches both the detail and the status-opties. ROLE_CREMATORIUM_EDIT is
// server-enforced — the buttons are always shown and a 403 surfaces inline.
function StatusCard({
  id,
  currentStatus,
  onChanged,
}: {
  id: string;
  currentStatus: string;
  onChanged: () => void;
}) {
  const t = useTranslate();
  const [chosen, setChosen] = useState("");
  const [opmerking, setOpmerking] = useState("");
  const [notice, setNotice] = useState<StatusNotice | null>(null);

  const { data, refetch } = useCustom<StatusOpties>({
    url: `crematorium/dossiers/${id}/status-opties`,
    method: "get",
  });
  const opties = data?.data;

  const { mutate: postStatus, isLoading: posting } = useCustomMutation();

  const submit = (naarStatus: string) => {
    if (!naarStatus) {
      return;
    }
    setNotice(null);
    postStatus(
      {
        url: `crematorium/dossiers/${id}/status`,
        method: "post",
        values: { naarStatus, opmerking: opmerking || null },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setChosen("");
          setOpmerking("");
          refetch();
          onChanged();
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setNotice(code === 409 ? "conflict" : code === 403 ? "forbidden" : "error");
        },
      },
    );
  };

  // Allowed targets exclude the current status; map to the shared crematies.status.* labels.
  const opties_select = (opties?.toegestaan ?? []).map((s) => ({
    value: s,
    label: t(`crematies.status.${s}`),
  }));
  const volgende = opties?.volgende ?? null;

  return (
    <section className="fawkes-relatie-subcard">
      <div className="crem-detail__status-card">
        <h5 className="fawkes-relatie-tile__subhead">{t("crematieDetail.status.title")}</h5>
        <div className="crem-detail__status-row">
          <StatusBadge tone={STATUS_TONE[currentStatus] ?? "neutral"}>
            {t(`crematies.status.${currentStatus}`)}
          </StatusBadge>
          {volgende && (
            <Button variant="primary" disabled={posting} onClick={() => submit(volgende)}>
              {t("crematieDetail.status.volgendeStap", {
                args: [t(`crematies.status.${volgende}`)],
              })}
            </Button>
          )}
        </div>

        {opties_select.length > 0 ? (
          <div className="crem-detail__status-form">
            <SelectField
              label={t("crematieDetail.status.wijzigen")}
              options={[{ value: "", label: "—" }, ...opties_select]}
              value={chosen}
              onChange={(e) => setChosen(e.target.value)}
            />
            <TextareaField
              label={t("crematieDetail.status.opmerking")}
              rows={2}
              value={opmerking}
              onChange={(e) => setOpmerking(e.target.value)}
            />
            <Button variant="secondary" disabled={!chosen || posting} onClick={() => submit(chosen)}>
              {t("crematieDetail.status.bevestig")}
            </Button>
          </div>
        ) : (
          <p className="fawkes-muted">{t("crematieDetail.status.geenActie")}</p>
        )}

        {notice && (
          <p className="fawkes-error">{t(`crematieDetail.status.${notice}`)}</p>
        )}
      </div>
    </section>
  );
}

type RegelNotice = "conflict" | "forbidden" | "error";

interface RegelForm {
  omschrijving: string;
  aantal: string;
  prijs: string;
  kortingPct: string;
  btwPercentage: string;
}

const EMPTY_FORM: RegelForm = {
  omschrijving: "",
  aantal: "1",
  prijs: "0",
  kortingPct: "0",
  btwPercentage: "21",
};

function toForm(r: Regel): RegelForm {
  return {
    omschrijving: r.omschrijving ?? "",
    aantal: r.aantal != null ? String(r.aantal) : "",
    prijs: r.prijs != null ? String(r.prijs) : "",
    kortingPct: r.kortingPct != null ? String(r.kortingPct) : "",
    btwPercentage: r.btwPercentage != null ? String(r.btwPercentage) : "",
  };
}

function num(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

// Crematorium (Epic 21, MR-W2): the editable Producten section. Add / edit / remove product lines
// via RELATIVE mutation urls; on success the parent refetches the detail so the rows and recomputed
// totals update. ROLE_CREMATORIUM_EDIT is server-enforced — a 403 surfaces inline, a 409 (stale
// version) prompts a refresh.
/**
 * "Concept verwijderen" — met de vraag ernaast in plaats van in een dialoog.
 *
 * <p>Verwijderen is niet terug te draaien, dus er hoort een tussenstap. Die staat hier op dezelfde
 * regel: één tik maakt er "Weet je het zeker?" van, met Ja en Nee ernaast. Dat leest sneller dan een
 * dialoog die het scherm overneemt, en op een telefoon scheelt het een venster dat over je werk
 * heen valt. Wie ernaast tikt heeft niets verwijderd.
 */
/** Het bestand ophalen; een mislukking mag niet stil zijn, maar ook niet het scherm breken. */
function haalBestand(dossierId: string, documentId: string, bestandsnaam: string) {
  downloadCrematieDocument(dossierId, documentId, bestandsnaam).catch(() => {
    // Bewust geen melding in de tegel: een download die niet lukt is bijna altijd een netwerkfout,
    // en de browser zegt dat zelf al. Wel in de console, zodat het te herleiden blijft.
    console.warn("Downloaden van crematiedocument mislukt", { dossierId, documentId });
  });
}

/**
 * "Uploaden" — een bestand aanleveren bij het dossier.
 *
 * <p>De knop is een label om een verborgen invoerveld heen: een bestandskiezer laat zich niet
 * openen vanuit code zonder dat de browser het als een echte klik ziet, en een label doet dat wel.
 * Zo blijft het één knop naast "Document maken" in plaats van een tweede rij.
 */
function DocumentUploaden({ id, onKlaar }: { id: string; onKlaar: () => void }) {
  const t = useTranslate();
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  return (
    <>
      <label className={`fui-button fui-button--secondary${bezig ? " is-disabled" : ""}`}>
        {bezig ? t("crematieDetail.documenten.bezig") : t("crematieDetail.documenten.uploaden")}
        <input
          type="file"
          hidden
          disabled={bezig}
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Het veld leegmaken, anders levert hetzelfde bestand tweemaal kiezen geen change op.
            e.target.value = "";
            if (!file) return;
            setFout(null);
            setBezig(true);
            uploadCrematieDocument(id, file)
              .then(() => onKlaar())
              .catch((err) =>
                setFout(
                  err instanceof CrematieDocumentError && err.serverMelding
                    ? err.serverMelding
                    : t("crematieDetail.documenten.uploadFout"),
                ),
              )
              .finally(() => setBezig(false));
          }}
        />
      </label>
      {fout && <span className="fawkes-error">{fout}</span>}
    </>
  );
}

function ConceptVerwijderen({ id, onWeg }: { id: string; onWeg: () => void }) {
  const t = useTranslate();
  const [vraagt, setVraagt] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const { mutate, isLoading: bezig } = useCustomMutation();

  if (!vraagt) {
    return (
      <>
        {fout && <span className="fawkes-error">{fout}</span>}
        <Button variant="secondary" onClick={() => { setFout(null); setVraagt(true); }}>
          {t("crematieDetail.concept.verwijderen")}
        </Button>
      </>
    );
  }
  return (
    <>
      <span className="fawkes-muted">{t("crematieDetail.concept.zekerWeten")}</span>
      <Button variant="secondary" disabled={bezig} onClick={() => setVraagt(false)}>
        {t("common.cancel")}
      </Button>
      <Button
        variant="secondary"
        disabled={bezig}
        onClick={() =>
          mutate(
            { url: `crematorium/dossiers/${id}`, method: "delete", values: {},
              successNotification: false, errorNotification: false },
            {
              onSuccess: () => onWeg(),
              onError: (err) => {
                const code = (err as { statusCode?: number }).statusCode;
                // 409 = het is geen concept meer, of er hangt een factuur aan. De api zegt waarom.
                setFout(t(code === 409 ? "crematieDetail.concept.nietMeer" : "crematieDetail.concept.fout"));
                setVraagt(false);
              },
            },
          )
        }
      >
        {t("crematieDetail.concept.jaVerwijder")}
      </Button>
    </>
  );
}

function ProductenCard({
  id,
  regels,
  euro,
  totals,
  onChanged,
  gefactureerd,
}: {
  id: string;
  regels: Regel[];
  euro: (v: number | null | undefined) => string;
  totals: ReactNode;
  onChanged: () => void;
  /**
   * Is er een factuur uit? Dan liggen de regels vast: het bedrag op de factuur is wat de klant
   * heeft gekregen, en een regel erbij of eraf zou de totalen laten schuiven zonder dat de factuur
   * meebeweegt. Corrigeren hoort via de factuur. De api weigert het ook — dit haalt alleen de
   * knoppen weg die toch nergens toe leiden.
   */
  gefactureerd: boolean;
}) {
  const t = useTranslate();
  const [editing, setEditing] = useState<Regel | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<RegelForm>(EMPTY_FORM);
  const [notice, setNotice] = useState<RegelNotice | null>(null);

  const { mutate, isLoading: saving } = useCustomMutation();

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setNotice(null);
    setAdding(true);
  };
  const openEdit = (r: Regel) => {
    setAdding(false);
    setEditing(r);
    setForm(toForm(r));
    setNotice(null);
  };
  const close = () => {
    setAdding(false);
    setEditing(null);
    setNotice(null);
  };

  const modalOpen = adding || editing != null;

  const onError = (err: unknown) => {
    const code = (err as { statusCode?: number }).statusCode;
    setNotice(code === 409 ? "conflict" : code === 403 ? "forbidden" : "error");
  };

  const save = () => {
    setNotice(null);
    const values = {
      omschrijving: form.omschrijving,
      aantal: num(form.aantal),
      prijs: num(form.prijs),
      kortingPct: num(form.kortingPct),
      btwPercentage: num(form.btwPercentage),
      version: editing?.version ?? null,
    };
    const isEdit = editing != null;
    mutate(
      {
        url: isEdit
          ? `crematorium/dossiers/${id}/regels/${editing.id}`
          : `crematorium/dossiers/${id}/regels`,
        method: isEdit ? "put" : "post",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          close();
          onChanged();
        },
        onError,
      },
    );
  };

  const remove = (r: Regel) => {
    if (!window.confirm(t("crematieDetail.regel.verwijderConfirm"))) {
      return;
    }
    setNotice(null);
    mutate(
      {
        url: `crematorium/dossiers/${id}/regels/${r.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onChanged(),
        onError,
      },
    );
  };

  const columns: Column<Regel>[] = [
    { header: t("crematieDetail.regel.omschrijving"), cell: (r) => r.omschrijving ?? "—" },
    { header: t("crematieDetail.regel.aantal"), align: "right", cell: (r) => r.aantal ?? "—" },
    { header: t("crematieDetail.regel.prijs"), align: "right", cell: (r) => euro(r.prijs) },
    {
      header: t("crematieDetail.regel.korting"),
      align: "right",
      cell: (r) => (r.kortingPct != null ? `${r.kortingPct}%` : "—"),
    },
    {
      header: t("crematieDetail.regel.btw"),
      align: "right",
      cell: (r) => (r.btwPercentage != null ? `${r.btwPercentage}%` : "—"),
    },
    { header: t("crematieDetail.regel.totaal"), align: "right", cell: (r) => euro(r.regelTotaal) },
    {
      header: t("crematieDetail.regel.acties"),
      align: "right",
      cell: (r) =>
        gefactureerd ? (
          <span className="fawkes-muted">—</span>
        ) : (
          <span className="crem-detail__row-actions">
            <Button variant="secondary" onClick={() => openEdit(r)}>
              {t("crematieDetail.regel.bewerken")}
            </Button>
            <Button variant="secondary" onClick={() => remove(r)}>
              {t("crematieDetail.regel.verwijderen")}
            </Button>
          </span>
        ),
    },
  ];

  return (
    <section className="fawkes-relatie-subcard">
      <div className="crem-detail__producten-header">
        <h5 className="fawkes-relatie-tile__subhead">{t("crematieDetail.section.producten")}</h5>
        {gefactureerd ? (
          <small className="fawkes-muted">{t("crematieDetail.regel.gefactureerdSlot")}</small>
        ) : (
          <Button variant="primary" onClick={openAdd}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("crematieDetail.regel.toevoegen")}
          </Button>
        )}
      </div>
      <IndeelbareTabel<Regel> tabelKey="crematie-dossier-detail-regel"
        columns={columns}
        rows={regels}
        getRowKey={(r) => r.id}
        empty={t("crematieDetail.regel.empty")}
      />
      <div className="crem-detail__totals">{totals}</div>

      {notice && !modalOpen && <p className="fawkes-error">{t(`crematieDetail.regel.${notice}`)}</p>}

      <Modal
        open={modalOpen}
        title={t(editing ? "crematieDetail.regel.edit.title" : "crematieDetail.regel.add.title")}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" disabled={saving} onClick={close}>
              {t("crematieDetail.regel.annuleren")}
            </Button>
            <Button
              variant="primary"
              disabled={saving || form.omschrijving.trim() === ""}
              onClick={save}
            >
              {t("crematieDetail.regel.opslaan")}
            </Button>
          </>
        }
      >
        <div className="crem-detail__regel-form">
          <Field
            label={t("crematieDetail.regel.veld.omschrijving")}
            value={form.omschrijving}
            onChange={(e) => setForm({ ...form, omschrijving: e.target.value })}
          />
          <Field
            label={t("crematieDetail.regel.veld.aantal")}
            type="number"
            value={form.aantal}
            onChange={(e) => setForm({ ...form, aantal: e.target.value })}
          />
          <Field
            label={t("crematieDetail.regel.veld.prijs")}
            type="number"
            value={form.prijs}
            onChange={(e) => setForm({ ...form, prijs: e.target.value })}
          />
          <Field
            label={t("crematieDetail.regel.veld.kortingPct")}
            type="number"
            value={form.kortingPct}
            onChange={(e) => setForm({ ...form, kortingPct: e.target.value })}
          />
          <Field
            label={t("crematieDetail.regel.veld.btw")}
            type="number"
            value={form.btwPercentage}
            onChange={(e) => setForm({ ...form, btwPercentage: e.target.value })}
          />
          {notice && <p className="fawkes-error">{t(`crematieDetail.regel.${notice}`)}</p>}
        </div>
      </Modal>
    </section>
  );
}

type BetalingNotice = "forbidden" | "error";

const BETAALWIJZEN = ["PIN", "Contant", "Bankoverschrijving", "iDEAL"];

// Crematorium (Epic 21, MR-W3): the editable Betalingen section. Register a (partial) payment or
// cancel one via RELATIVE mutation urls; on success the parent refetches the detail so the rows and
// the recomputed roll-up (betaald / openstaand / betaalstatus) update. ROLE_CREMATORIUM_EDIT is
// server-enforced — a 403 surfaces inline.
function BetalingCard({
  id,
  betalingen,
  euro,
  dateTime,
  onChanged,
}: {
  id: string;
  betalingen: Betaling[];
  euro: (v: number | null | undefined) => string;
  dateTime: (v: string | null | undefined) => string;
  onChanged: () => void;
}) {
  const t = useTranslate();
  const [adding, setAdding] = useState(false);
  const [bedrag, setBedrag] = useState("");
  const [wijze, setWijze] = useState(BETAALWIJZEN[0]);
  const [notice, setNotice] = useState<BetalingNotice | null>(null);

  const { mutate, isLoading: saving } = useCustomMutation();

  const openAdd = () => {
    setBedrag("");
    setWijze(BETAALWIJZEN[0]);
    setNotice(null);
    setAdding(true);
  };
  const close = () => {
    setAdding(false);
    setNotice(null);
  };

  const onError = (err: unknown) => {
    const code = (err as { statusCode?: number }).statusCode;
    setNotice(code === 403 ? "forbidden" : "error");
  };

  const bedragNum = num(bedrag);

  const save = () => {
    setNotice(null);
    mutate(
      {
        url: `crematorium/dossiers/${id}/betalingen`,
        method: "post",
        values: { bedrag: bedragNum, betaalwijze: wijze },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          close();
          onChanged();
        },
        onError,
      },
    );
  };

  const remove = (b: Betaling) => {
    if (!window.confirm(t("crematieDetail.betaling.verwijderConfirm"))) {
      return;
    }
    setNotice(null);
    mutate(
      {
        url: `crematorium/dossiers/${id}/betalingen/${b.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onChanged(),
        onError,
      },
    );
  };

  const columns: Column<Betaling>[] = [
    { header: t("crematieDetail.betaling.datum"), cell: (r) => dateTime(r.betaaldOp) },
    { header: t("crematieDetail.betaling.wijze"), cell: (r) => r.betaalwijze ?? "—" },
    { header: t("crematieDetail.betaling.bedrag"), align: "right", cell: (r) => euro(r.bedrag) },
    { header: t("crematieDetail.betaling.door"), cell: (r) => r.geregistreerdDoor ?? "—" },
    {
      header: t("crematieDetail.betaling.acties"),
      align: "right",
      cell: (r) => (
        <span className="crem-detail__row-actions">
          <Button variant="secondary" onClick={() => remove(r)}>
            {t("crematieDetail.betaling.verwijderen")}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <section className="fawkes-relatie-subcard">
      <div className="crem-detail__producten-header">
        <h5 className="fawkes-relatie-tile__subhead">{t("crematieDetail.section.betalingen")}</h5>
        <Button variant="primary" onClick={openAdd}>
          {t("crematieDetail.betaling.registreren")}
        </Button>
      </div>
      <IndeelbareTabel<Betaling> tabelKey="crematie-dossier-detail-betaling"
        columns={columns}
        rows={betalingen}
        getRowKey={(r) => r.id}
        empty={t("crematieDetail.betalingen.empty")}
      />

      {notice && !adding && <p className="fawkes-error">{t(`crematieDetail.betaling.${notice}`)}</p>}

      <Modal
        open={adding}
        title={t("crematieDetail.betaling.add.title")}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" disabled={saving} onClick={close}>
              {t("crematieDetail.betaling.annuleren")}
            </Button>
            <Button
              variant="primary"
              disabled={saving || bedragNum == null || bedragNum <= 0}
              onClick={save}
            >
              {t("crematieDetail.betaling.opslaan")}
            </Button>
          </>
        }
      >
        <div className="crem-detail__regel-form">
          <Field
            label={t("crematieDetail.betaling.veld.bedrag")}
            type="number"
            value={bedrag}
            onChange={(e) => setBedrag(e.target.value)}
          />
          <SelectField
            label={t("crematieDetail.betaling.veld.wijze")}
            options={BETAALWIJZEN.map((w) => ({ value: w, label: w }))}
            value={wijze}
            onChange={(e) => setWijze(e.target.value)}
          />
          {notice && <p className="fawkes-error">{t(`crematieDetail.betaling.${notice}`)}</p>}
        </div>
      </Modal>
    </section>
  );
}

/**
 * Het uitvaartdossier (mockup): een dossierkop met wie, wat er nu moet gebeuren en de factuurstand,
 * en daaronder de tegels — dier, crematie, vervoer, factuurregels, handelingen en logboek — die
 * elk een formulier zijn met hun eigen Annuleren/Opslaan. Geen slot, geen leesstand: een dossier
 * is werk in uitvoering. Onder de klant (tabblad Uitvaart) staat het ingebed; los, vanuit de
 * lijsten, draagt de kop de weg terug en de weg naar de klant.
 */
export function CrematieDossierDetail({
  id,
  onBack,
  ingebed = false,
  onOpenKlant,
  verhuisNaarKlant,
}: {
  id: string;
  onBack?: () => void;
  /** Ingebed op het klantscherm: geen eigen terugknop en geen knop naar de klant. */
  ingebed?: boolean;
  /** Vanaf het losse dossierscherm: de klant openen, met dit dossier op zijn tabblad Uitvaart. */
  onOpenKlant?: (relatieId: number) => void;
  /**
   * Zodra blijkt dat dit dossier een klant heeft, hoort het daar te staan — met de klantkop en
   * zijn tabbladen erboven, zoals de mockup. Een deeplink komt hier binnen zonder die klant te
   * kennen; die wordt pas bekend als het dossier geladen is.
   */
  verhuisNaarKlant?: (relatieId: number) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<DossierDetail>({
    url: `crematorium/dossiers/${id}`,
    method: "get",
  });
  const d = data?.data;
  const relatieVanDossier = d?.relatieId ?? null;
  useEffect(() => {
    if (!ingebed && relatieVanDossier != null) verhuisNaarKlant?.(relatieVanDossier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingebed, relatieVanDossier]);
  const stappen = useDossierStappen(id);
  // Twee tabbladen onder de kop (mockup): het dossier zelf, en de documenten erbij.
  const [subTab, setSubTab] = useState<"dossier" | "documenten" | "afdrukken">("dossier");
  // Uitgeven: de factuur vastleggen zoals het dossier nu optelt. Een tweede keer is een conflict.
  const [uitgifteFout, setUitgifteFout] = useState<"conflict" | "forbidden" | "error" | null>(null);
  const { mutate: geefUit, isLoading: uitgeven } = useCustomMutation();
  // De naam van de vestiging, voor de kop van de afdrukken.
  const { data: vestigingData } = useCustom({ url: "vestigingen/current", method: "get", errorNotification: false });
  const organisatie = ((vestigingData?.data as { naam?: string } | undefined)?.naam) ?? "";
  const euro = (v: number | null | undefined) => (v != null ? formatEuro(v, locale) : "—");
  const date = (v: string | null | undefined) => (v ? formatDate(v, locale) : "—");
  const dateTime = (v: string | null | undefined) => (v ? formatDateTime(v, locale) : "—");

  if (isLoading) {
    return (
      <FormCard>
        <p className="fawkes-muted">{t("common.loading")}</p>
      </FormCard>
    );
  }
  if (isError || !d) {
    return (
      <FormCard>
        {onBack && (
          <Button variant="secondary" onClick={onBack}>
            {t("common.back")}
          </Button>
        )}
        <p className="fawkes-error">{t("crematieDetail.load.error")}</p>
      </FormCard>
    );
  }

  const nummer = d.dossierNummer || d.conceptNummer || "—";

  // Een concept weghalen dat niet doorgaat. Alleen zolang het een concept is: daarna heeft het een
  // nummer, staat het in overzichten en is Annuleren het juiste antwoord. De api bewaakt dezelfde
  // grens en antwoordt met 409; dit voorkomt alleen dat je ertegenaan loopt.
  const isConcept = d.status === "CONCEPT";

  const factuurColumns: Column<FactuurRegel>[] = [
    { header: t("crematies.factuur.col.nummer"), cell: (r) => r.factuurNummer ?? "—" },
    { header: t("crematies.factuur.col.factuurdatum"), cell: (r) => date(r.factuurDatum) },
    { header: t("crematies.factuur.col.vervaldatum"), cell: (r) => date(r.vervaldatum) },
    {
      header: t("crematies.factuur.col.status"),
      cell: (r) => (r.betaalStatus ? t(`crematies.betaal.${r.betaalStatus}`) : "—"),
    },
    { header: t("crematies.factuur.col.totaal"), align: "right", cell: (r) => euro(r.totaalBedrag) },
    { header: t("crematies.factuur.col.openstaand"), align: "right", cell: (r) => euro(r.openstaandBedrag) },
  ];

  const documentColumns: Column<Document>[] = [
    {
      header: t("crematieDetail.document.bestand"),
      cell: (r) =>
        r.bestandsnaam ? (
          <button
            type="button"
            className="fui-button fui-button--secondary"
            onClick={() => haalBestand(id, r.id, r.bestandsnaam ?? "document")}
          >
            {t("crematieDetail.document.downloaden")}
          </button>
        ) : (
          <span className="fawkes-muted">—</span>
        ),
    },
    { header: t("crematieDetail.document.type"), cell: (r) => r.type ?? "—" },
    { header: t("crematieDetail.document.omschrijving"), cell: (r) => r.omschrijving ?? "—" },
    { header: t("crematieDetail.document.kanaal"), cell: (r) => r.kanaal ?? "—" },
    { header: t("crematieDetail.document.aangemaakt"), cell: (r) => dateTime(r.aangemaaktOp) },
    { header: t("crematieDetail.document.door"), cell: (r) => r.aangemaaktDoor ?? "—" },
  ];

  const icoon = (Icon: typeof Flame) => <Icon size={18} aria-hidden="true" />;
  const herlaad = () => {
    refetch();
    stappen.refetch();
  };

  const afdrukDossier: AfdrukDossier = {
    dossierNummer: d.dossierNummer ?? d.conceptNummer,
    dierNaam: d.dier?.naam ?? null,
    diersoort: d.dier?.diersoort ?? null,
    ras: d.dier?.ras ?? null,
    gewichtKg: d.dier?.gewichtKg ?? null,
    chipnummer: d.dier?.chipTatoeage ?? null,
    datumOverlijden: d.dier?.datumOverlijden ?? null,
    verwerkingsvorm: d.verwerkingsvorm,
    uitvaartvormNaam: d.verwerkingsvorm ? t(`crematoriumTarief.vorm.${d.verwerkingsvorm}`) : null,
    planningDatum: d.planningDatum,
    identificatieNummer: d.identificatieNummer,
    asUrnNummer: d.asUrnNummer,
    retourwijze: d.retourwijze,
    grafnummer: d.grafnummer,
    eigenaarNaam: d.eigenaar?.naam ?? null,
    eigenaarAdres: d.eigenaar?.adres ?? null,
    eigenaarPostcode: d.eigenaar?.postcode ?? null,
    eigenaarPlaats: d.eigenaar?.plaats ?? null,
    organisatie,
    regels: d.regels.map((r) => ({ omschrijving: r.omschrijving, aantal: r.aantal, prijs: r.prijs, btwPercentage: r.btwPercentage, regelTotaal: r.regelTotaal })),
    subtotaal: d.subtotaal,
    btwTotaal: d.btwTotaal,
    totaalBedrag: d.totaalBedrag,
    betaaldBedrag: d.betaaldBedrag,
    openstaandBedrag: d.openstaandBedrag,
    factuur: d.facturen[0] ? { factuurNummer: d.facturen[0].factuurNummer, factuurDatum: d.facturen[0].factuurDatum, vervaldatum: d.facturen[0].vervaldatum } : null,
  };

  const geld = (label: string, bedrag: number | null | undefined, extra?: string) => (
    <div className={"crem-money__row" + (extra ? ` crem-money__row--${extra}` : "")}>
      <span>{label}</span>
      <strong>{euro(bedrag)}</strong>
    </div>
  );

  return (
    /* crem-page draagt de mockupkleuren: getinte subkaarten en icoontjes in de tegelkleur. */
    <div className="crem-detail crem-page">
      {/* De dossierkop: wie het is, wat er nu moet gebeuren, en hoe het financieel staat. Die drie
          horen bij het dossier als geheel en blijven daarom boven de tegels staan. */}
      <div className="crem-dossierbar">
        <div className="crem-dossierbar__left">
          <div className="crem-dossierbar__ident">
            <span className="crem-dossierbar__icon" aria-hidden="true"><Flame size={22} /></span>
            <div className="crem-dossierbar__body">
              <div className="crem-dossierbar__title">
                {d.dier?.naam ?? "—"}{" "}
                <StatusBadge tone={STATUS_TONE[d.status] ?? "neutral"}>
                  {t(`crematies.status.${d.status}`)}
                </StatusBadge>
              </div>
              {/* Losse kernwoorden in plaats van een doorlopende regel: zo vind je "welke vorm" of
                  "wanneer" in één oogopslag terug. */}
              <dl className="crem-feiten">
                <dt>{t("crematieDetail.kop.dossier")}</dt>
                <dd>{nummer}</dd>
                <dt>{t("crematieDetail.kop.dier")}</dt>
                <dd>
                  {[d.dier?.diersoort, d.dier?.naam, d.dier?.gewichtKg != null ? `${d.dier.gewichtKg} kg` : null]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </dd>
                <dt>{t("crematieDetail.kop.vorm")}</dt>
                <dd>{d.verwerkingsvorm ? t(`crematoriumTarief.vorm.${d.verwerkingsvorm}`) : "—"}</dd>
                <dt>{t("crematieDetail.kop.uitvaartdatum")}</dt>
                <dd>{dateTime(d.planningDatum)}</dd>
                <dt>{t("crematieDetail.kop.herkomst")}</dt>
                <dd>{d.herkomst ? t(`crematies.herkomst.${d.herkomst}`) : "—"}</dd>
                <dt>{t("crematieDetail.kop.totaal")}</dt>
                <dd>
                  {euro(d.totaalBedrag)}
                  {d.openstaandBedrag != null && d.openstaandBedrag > 0 && (
                    <span className="crem-dossierbar__open">
                      {" · "}
                      {t("crematieDetail.kop.openstaand", { args: [euro(d.openstaandBedrag)] })}
                    </span>
                  )}
                </dd>
              </dl>
            </div>
          </div>
          {/* Los geopend (vanuit een lijst) draagt de kop de weg terug en de weg naar de klant;
              onder de klant zelf is dat dubbelop. */}
          {!ingebed && (onBack || (onOpenKlant && d.relatieId != null)) && (
            <div className="crem-dossierbar__acties">
              {onBack && (
                <Button variant="secondary" onClick={onBack}>
                  ‹ {t("common.back")}
                </Button>
              )}
              {onOpenKlant && d.relatieId != null && (
                <Button variant="primary" onClick={() => onOpenKlant(d.relatieId as number)}>
                  {t("crematieDetail.klantOpenen")}
                </Button>
              )}
            </div>
          )}
        </div>

        <VolgendeStapPaneel stappen={stappen.stappen} onAfgerond={herlaad} />

        <CrematieDossierFactuurblok
          id={id}
          onOpgeslagen={herlaad}
          opvolging={{
            herinneringOp: d.herinneringOp,
            belmomentOp: d.belmomentOp,
            incassoOp: d.incassoOp,
            factuurPerEmail: d.factuurPerEmail,
          }}
          factuurNummer={d.facturen[0]?.factuurNummer ?? null}
          betaalStatus={d.betaalStatus}
          factuurDatum={d.facturen[0]?.factuurDatum ?? null}
          totaal={d.totaalBedrag}
          betaald={d.betaaldBedrag}
          openstaand={d.openstaandBedrag}
        />
      </div>

      <div className="crem-detail__subtabs">
        <Tabs
          tabs={[
            { key: "dossier", label: t("crematieDetail.tab.dossier") },
            { key: "documenten", label: t("crematieDetail.tab.documenten") },
            { key: "afdrukken", label: t("crematieDetail.tab.afdrukken") },
          ]}
          activeKey={subTab}
          onChange={(k) => setSubTab(k as "dossier" | "documenten" | "afdrukken")}
          ariaLabel={t("crematieDetail.tab.dossier")}
        />
      </div>

      {subTab === "dossier" && (
        /* Het tegelbord: dier, crematie, vervoer, factuurregels, handelingen en logboek naast elkaar
           in beeld, elk een formulier met zijn eigen Annuleren/Opslaan. */
        <div className="fawkes-relatie-detail">
          <div className="fui-tile-grid">
            <CrematieDossierDierTegel
              id={id}
              onOpgeslagen={herlaad}
              gegevens={{
                naam: d.dier?.naam ?? null,
                diersoort: d.dier?.diersoort ?? null,
                ras: d.dier?.ras ?? null,
                geslacht: d.dier?.geslacht ?? null,
                kleur: d.dier?.kleur ?? null,
                gewichtKg: d.dier?.gewichtKg ?? null,
                chipTatoeage: d.dier?.chipTatoeage ?? null,
                datumOverlijden: d.dier?.datumOverlijden ?? null,
                geboortedatum: d.dier?.geboortedatum ?? null,
                kruising: d.dier?.kruising ?? null,
                kruisingMet: d.dier?.kruisingMet ?? null,
                gewichtsklasseId: d.gewichtsklasseId,
                gewichtsklasseNaam: d.gewichtsklasseNaam,
                gewichtsklasseHandmatig: d.gewichtsklasseHandmatig,
              }}
            />

            <CrematieDossierCrematieTegel
              id={id}
              onOpgeslagen={herlaad}
              gegevens={{
                uitvaartvormId: d.uitvaartvormId,
                verwerkingsvorm: d.verwerkingsvorm,
                ingevoerdDoor: d.ingevoerdDoor,
                planningDatum: d.planningDatum,
                crematoriumLocatie: d.crematoriumLocatie,
                identificatieNummer: d.identificatieNummer,
                asUrnNummer: d.asUrnNummer,
                bijzonderheden: d.bijzonderheden,
                retourwijze: d.retourwijze,
                grafnummer: d.grafnummer,
                gewichtsklasseNaam: d.gewichtsklasseNaam,
                gewichtsklasseId: d.gewichtsklasseId,
                tariefId: d.tariefId,
                tariefWijzigbaar: d.tariefWijzigbaar,
                tarief: tariefRegel(d.regels),
              }}
            />

            <CrematieDossierVervoerTegel
              id={id}
              onOpgeslagen={herlaad}
              gegevens={{
                herkomst: d.herkomst,
                spoed: d.spoed,
                ophaalAdres: d.ophaalAdres,
                ophaalAdresSoort: d.ophaalAdresSoort,
                ophaalDatum: d.ophaalDatum,
                ophaalTijd: d.ophaalTijd,
                afstandKm: d.afstandKm,
                retourRekenen: d.retourRekenen,
                chauffeur: d.chauffeur,
                ritNummer: d.ritNummer,
                ontvangenDoor: d.ontvangenDoor,
                dierenartsRelatieId: d.dierenartsRelatieId,
                derdePartijRelatieId: d.derdePartijRelatieId,
                ritKosten: d.ritKosten,
                eigenaar: d.eigenaar ? { adres: d.eigenaar.adres, postcode: d.eigenaar.postcode, plaats: d.eigenaar.plaats } : null,
              }}
            />

            {/* Factuurregels: de regels, het totaal en de betalingen bij elkaar — een aanbetaling plus
                de rest paste niet in losse velden "betaalwijze" en "betaaldatum". */}
            <section className="fui-tile crem-tile crem-tile--factuur">
              <header className="fui-tile__header">
                <h4 className="fui-tile__title">
                  <span className="fawkes-relatie-tiletitel">{icoon(Receipt)}{t("crematieDetail.section.factuurregels")}</span>
                </h4>
                {d.betaalStatus && (
                  <div className="fui-tile__actions">
                    <StatusBadge tone="neutral">{t(`crematies.betaal.${d.betaalStatus}`)}</StatusBadge>
                  </div>
                )}
              </header>
              <div className="fawkes-relatie-tile fawkes-relatie-tile--fill">
                <ProductenCard
                  id={id}
                  regels={d.regels}
                  euro={euro}
                  onChanged={herlaad}
                  gefactureerd={d.facturen.length > 0}
                  totals={
                    <div className="crem-money crem-money--breed">
                      {geld(t("crematieDetail.totaal.subtotaal"), d.subtotaal)}
                      {geld(t("crematieDetail.totaal.korting"), d.kortingTotaal)}
                      {geld(t("crematieDetail.totaal.btw"), d.btwTotaal)}
                      {geld(t("crematieDetail.totaal.totaal"), d.totaalBedrag, "total")}
                      {geld(t("crematieDetail.totaal.betaald"), d.betaaldBedrag)}
                      {geld(t("crematieDetail.totaal.openstaand"), d.openstaandBedrag, "open")}
                    </div>
                  }
                />
                <BetalingCard id={id} betalingen={d.betalingen} euro={euro} dateTime={dateTime} onChanged={herlaad} />
                {uitgifteFout && (
                  <p className="fawkes-error" role="alert">{t(`crematieDetail.factuur.${uitgifteFout}`)}</p>
                )}
                {d.facturen.length > 0 && (
                  <section className="fawkes-relatie-subcard">
                    <h5 className="fawkes-relatie-tile__subhead">{t("crematieDetail.section.facturen")}</h5>
                    <IndeelbareTabel<FactuurRegel> tabelKey="crematie-dossier-detail-factuur-regel"
                      columns={factuurColumns}
                      rows={d.facturen}
                      getRowKey={(r) => r.id}
                      empty={t("crematieDetail.facturen.empty")}
                    />
                  </section>
                )}
              </div>
              {/* Uitgeven legt de regels vast als factuur; daarna kan het alleen nog via een correctie. */}
              <ActionBar>
                {isConcept && (
                  <ConceptVerwijderen id={id} onWeg={() => (onBack ? onBack() : herlaad())} />
                )}
                <Button variant="secondary" onClick={() => drukAf(afdrukHtml("FACTUUR", afdrukDossier))}>
                  {t("crematieDetail.factuur.voorbeeld")}
                </Button>
                <Button
                  disabled={uitgeven || d.facturen.length > 0 || !d.dossierNummer}
                  title={d.facturen.length > 0 ? t("crematieDetail.factuur.alUitgegeven") : undefined}
                  onClick={() => {
                    setUitgifteFout(null);
                    geefUit(
                      { url: `crematorium/dossiers/${id}/factuur/uitgeven`, method: "post", values: {}, successNotification: false, errorNotification: false },
                      {
                        onSuccess: () => herlaad(),
                        onError: (err) => {
                          const code = (err as { statusCode?: number }).statusCode;
                          setUitgifteFout(code === 409 ? "conflict" : code === 403 ? "forbidden" : "error");
                        },
                      },
                    );
                  }}
                >
                  {t("crematieDetail.factuur.uitgeven")}
                </Button>
              </ActionBar>
            </section>

            <HandelingenTegel stappen={stappen.stappen} isLoading={stappen.isLoading} onGewijzigd={herlaad} />

            {/* Logboek: de statusgeschiedenis en de identificatiecontroles, en de statuswissel zelf. */}
            <section className="fui-tile crem-tile crem-tile--log">
              <header className="fui-tile__header">
                <h4 className="fui-tile__title">
                  <span className="fawkes-relatie-tiletitel">{icoon(History)}{t("crematieDetail.section.logboek")}</span>
                </h4>
              </header>
              <div className="fawkes-relatie-tile fawkes-relatie-tile--fill">
                {/* Eén tijdlijn zoals het logboek bij het dier: statuswissels, controles, afgeronde
                    handelingen en betalingen door elkaar, met een gekleurde tag per soort. */}
                <section className="fawkes-relatie-subcard">
                  <CrematieDossierLogboek
                    statuslog={d.statuslog}
                    controles={d.controles}
                    betalingen={d.betalingen}
                    stappen={stappen.stappen}
                  />
                </section>
                <StatusCard id={id} currentStatus={d.status} onChanged={herlaad} />
              </div>
            </section>
          </div>
        </div>
      )}


      {subTab === "documenten" && (
        <div className="fawkes-relatie-detail">
          <Tile title={t("crematieDetail.section.documenten")} className="fui-tile--breed">
            <p className="crem-aanmelding__concept">
              <Button variant="primary" onClick={() => setSubTab("afdrukken")}>+ {t("crematieDetail.documenten.maken")}</Button>
              {/* Aanleveren naast maken: een ondertekende verklaring die per post terugkomt of een
                  scan van de dierenarts hoort bij het dossier, maar kwam nergens terecht. */}
              <DocumentUploaden id={id} onKlaar={herlaad} />
            </p>
            <IndeelbareTabel<Document> tabelKey="crematie-dossier-detail-document"
              columns={documentColumns}
              rows={d.documenten}
              getRowKey={(r) => r.id}
              empty={t("crematieDetail.documenten.empty")}
            />
          </Tile>
        </div>
      )}

      {subTab === "afdrukken" && (
        <CrematieDossierAfdrukken id={id} dossier={afdrukDossier} onGemaakt={herlaad} />
      )}
    </div>
  );
}
